// @flow

import * as React from 'react';
import classNames from 'classnames';

import Emojify from 'app/components/shared/Emojify';
import FriendlyTime from 'app/components/shared/FriendlyTime';
import { AttributeTable, AttributeTableRow } from 'app/components/shared/AttributeTable';

import AgentStore from 'app/stores/AgentStore';
import JobInformationStore from 'app/stores/JobInformationStore';

import Database from 'app/lib/Database';

import UploadedJobLink from './UploadedJobLink';

type StepUpload = {
  uuid: string,
  createdAt: string,
  definition: string,
  jobUuids: Array<string>,
  processedAt: string,
  rejectionMessage: string,
  replaceExistingSteps: ?boolean
};

type Props = {
  job: {
    id: string,
    state: string,
    basePath: string,
    agentAcceptedAt: ?string,
    agentAssignedAt: ?string,
    canceledAt: ?string,
    createdAt: ?string,
    finishedAt: ?string,
    runnableAt: ?string,
    startedAt: ?string,
    timedOutAt: ?string,
    canceledBy: ?{
      name: string
    },
    step: ?{
      conditional: ?string
    },
    agentName: string,
    agentQueryRules: Array<string>,
    agentMetaData: Array<string>,
    agentVersion: ?string,
    agentVersionKnownIssues: ?boolean,
    agentPath: string,
    agentHostname: string,
    concurrencyGroup: string,
    stepKey: string,
    concurrency: number,
    dependencies: Array<string>
  }
};

type State = {
  stepUploads: Array<StepUpload>
};

const getStateFromStore = (jobId) => Database.parse(JobInformationStore.get(jobId, "uploads"));

export default class JobTimeline extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      stepUploads: getStateFromStore(props.job.id)
    };
  }

  refetchStore() {
    JobInformationStore.refetch(this.props.job.id, "uploads", this.props.job.basePath + "/uploads");
  }

  handleStoreChange = () => {
    this.setState({ stepUploads: getStateFromStore(this.props.job.id) });
  }

  componentDidMount() {
    JobInformationStore.addChangeListener(this.handleStoreChange);
    this.refetchStore();
  }

  componentWillUnmount() {
    JobInformationStore.removeChangeListener(this.handleStoreChange);
  }

  render() {
    const events = [];

    if (this.props.job.createdAt) {
      const normalizedQuery = AgentStore.normalizedQuery(this.props.job.agentQueryRules);
      events.push({
        date: this.props.job.createdAt,
        node: (
          <div>
            <div className="mb1">
              Job created:
            </div>
            <AttributeTable>
              <AttributeTableRow title="Agent Targeting Rules">
                {normalizedQuery.map((query) => <div key="query">{query}</div>)}
              </AttributeTableRow>
              {this.props.job.concurrencyGroup && (
                <React.Fragment>
                  <AttributeTableRow title="Concurrency Group">
                    {this.props.job.concurrencyGroup}
                  </AttributeTableRow>
                  <AttributeTableRow title="Concurrency Limit">
                    {this.props.job.concurrency}
                  </AttributeTableRow>
                </React.Fragment>
              )}
              {this.props.job.dependencies && (
                <AttributeTableRow title="Dependencies">
                  <React.Fragment>
                    {this.props.job.dependencies.map((dep) => <div key="dep">{dep}</div>)}
                  </React.Fragment>
                </AttributeTableRow>
              )}
              {this.props.job.stepKey && (
                <AttributeTableRow title="Step Key">
                  {this.props.job.stepKey}
                </AttributeTableRow>
              )}
              {this.props.job.step && this.props.job.step.conditional && (
                <AttributeTableRow title="Conditional">
                  {this.props.job.step.conditional}
                </AttributeTableRow>
              )}
            </AttributeTable>
          </div>
        )
      });
    }

    if (this.props.job.state === 'limited') {
      events.push({
        date: this.props.job.createdAt,
        node: (
          <div>
            <div className="mb1">
              Waiting on concurrency group:
            </div>
            <AttributeTable>
              <AttributeTableRow title="Concurrency Group">
                {this.props.job.concurrencyGroup}
              </AttributeTableRow>
              <AttributeTableRow title="Concurrency Limit">
                {this.props.job.concurrency}
              </AttributeTableRow>
            </AttributeTable>
          </div>
        )
      });
    }

    if (this.props.job.runnableAt && (this.props.job.agentName || (this.props.job.state === 'scheduled'))) {
      events.push({
        date: this.props.job.runnableAt,
        node: <div>Scheduled to be run on an agent</div>
      });
    }

    if (this.props.job.agentAssignedAt) {
      events.push({
        date: this.props.job.agentAssignedAt,
        node: (
          <div>
            <div className="mb1">Assigned to agent:</div>
            {this.renderAgentNode()}
          </div>
        )
      });
    }

    if (this.props.job.agentAcceptedAt) {
      events.push({
        date: this.props.job.agentAcceptedAt,
        node: <div>Accepted by agent</div>
      });
    }

    if (this.props.job.startedAt) {
      events.push({
        date: this.props.job.startedAt,
        node: <div>Job started</div>
      });
    }

    if (this.state.stepUploads && this.state.stepUploads.length) {
      this.state.stepUploads.forEach((stepUpload) => {
        events.push({
          date: stepUpload.createdAt,
          node: this.renderStepUploadCreation(stepUpload)
        });

        if (stepUpload.jobUuids && stepUpload.jobUuids.length) {
          events.push({
            date: stepUpload.processedAt,
            node: this.renderStepUploadJobs(stepUpload)
          });
        }

        if (stepUpload.rejectionMessage) {
          return events.push({
            date: stepUpload.processedAt,
            node: this.renderStepUploadRejection(stepUpload)
          });
        }
      });
    }

    if (this.props.job.canceledAt) {
      if (this.props.job.canceledBy) {
        events.push({
          date: this.props.job.canceledAt,
          node: <div>Job cancelled by {this.props.job.canceledBy.name}</div>
        });
      } else {
        events.push({
          date: this.props.job.canceledAt,
          node: <div>Job cancelled</div>
        });
      }
    }

    if (this.props.job.timedOutAt) {
      events.push({
        date: this.props.job.timedOutAt,
        node: <div>Job timed out</div>
      });
    }

    if (this.props.job.finishedAt) {
      events.push({
        date: this.props.job.finishedAt,
        node: <div>Job finished</div>
      });
    }

    return (
      <div data-testid="JobTimelineComponent" className="mt1">
        {events.map((event, index) => (
          (event.date ? (
            <div key={index} className={classNames({ mt3: index !== 0 })}>
              <small className="dark-gray mb1 block">
                <FriendlyTime value={event.date} capitalized={true} seconds={true} />
              </small>
              {event.node}
            </div>
          ) : null)
        ))}
      </div>
    );
  }

  renderStepUploadRejection(stepUpload: StepUpload) {
    return (
      <div className="mt1">
        <div className="mb1">
          Pipeline upload <code>{stepUpload.uuid}</code> failed:
        </div>
        <code className="" style={{}}>
          <pre className="p2 code">
            {stepUpload.rejectionMessage}
          </pre>
        </code>
      </div>
    );
  }

  renderStepUploadJobs(stepUpload: StepUpload) {
    let positionInformation = "after the current job";

    if (stepUpload.replaceExistingSteps) {
      positionInformation += " replacing the rest of the pipeline";
    }

    const buildUrl = window.location.toString().replace(/(#.*)?$/, '');

    return (
      <div className="mt1">
        <details>
          <summary className="mb1 cursor-pointer">
            {`${stepUpload.jobUuids.length} ${stepUpload.jobUuids.length === 1 ? 'job was' : 'jobs were'} added to the build ${positionInformation}`}
          </summary>
          <ul>
            {stepUpload.jobUuids.map((uuid) => <UploadedJobLink key={uuid} uuid={uuid} url={`${buildUrl}#${uuid}`} />)}
          </ul>
        </details>
      </div>
    );
  }

  renderStepUploadCreation(stepUpload: StepUpload) {
    return (
      <div>
        <details dataPipelineId={stepUpload.uuid}>
          <summary className="mb1 cursor-pointer">
            Pipeline uploaded
          </summary>
          <div dangerouslySetInnerHTML={{ __html: stepUpload.definition }} />
        </details>
      </div>
    );
  }

  renderAgentNode() {
    const metaDataNode = (
      this.props.job.agentMetaData && (this.props.job.agentMetaData.length > 0)
        ? (
          this.props.job.agentMetaData.sort().join(" ")
        )
        : (
          "No metadata"
        )
    );

    const version = (
      this.props.job.agentVersion
        ? `v${this.props.job.agentVersion}`
        : ""
    );

    return (
      <div
        className="rounded border border-gray block clearfix mt1 p2"
        style={{ minWidth: "40%", maxWidth: "750px" }}
      >
        <div className="pull-left text-left line-height-1">
          <div>
            <a href={this.props.job.agentPath}>
              <Emojify text={this.props.job.agentName} />
            </a>
          </div>
          <div className="h5 regular m0 mt1 dark-gray">
            {metaDataNode}
          </div>
        </div>
        <div className="pull-right text-right line-height-1">
          <div>
            {version}
            {this.props.job.agentVersionKnownIssues &&
              <a
                className="ml1 text-decoration-none"
                href={this.props.job.agentPath}
                title={`This version has known issues`}
              >
                ⚠️
              </a>
            }
          </div>
          <div className="h5 regular m0 mt1 dark-gray">
            {this.props.job.agentHostname}
          </div>
        </div>
      </div>
    );
  }
}
